import {ReactNode, useCallback, useState, useRef, useLayoutEffect} from 'react';

import {useMediaQuery} from 'react-responsive';
import styled, {CSSProperties} from 'styled-components';

import {BodyText, BodyTypes, TextThemes} from 'components/design-guide';
import BannerActionWrapper from 'shared/components/MessageBanner/BannerActionWrapper';
import DismissButton from 'shared/components/MessageBanner/DismissButton';
import Icon from 'shared/components/MessageBanner/Icon';
import {BannerType} from 'shared/components/MessageBanner/Themes';
import Wrapper from 'shared/components/MessageBanner/Wrapper';
import {intlFormatSingleMessage} from 'shared/IntlFormatMessage/utils/intlMesssageFormat';
import {Breakpoints, FontWeight, Spacing} from 'styles/Base';
import TestProps from 'types/TestProps';

interface Props extends TestProps {
  type: BannerType;
  heading?: ReactNode;
  subtext?: ReactNode;
  actionButton?: ReactNode;
  noMargin?: boolean;
  contextual?: boolean;
  dismissable?: boolean;
  autoScroll?: boolean;
  dismissAriaLabel?: string;
  onDismiss?(): void;
  inline?: boolean;
  padding?: string;
  customVisibilityHandler?: boolean;
  borderStyle?: CSSProperties;
  id?: string;
}

type ContentProps = Pick<Props, 'contextual'> & {
  children?: ReactNode;
};

const Content = styled(({children, contextual, ...rest}: ContentProps) => <div {...rest}>{children}</div>)`
  align-items: center;
  display: flex;
  justify-content: space-between;
  ${({contextual}) => !contextual && `max-width: 800px`};
  flex-grow: 1;
  overflow-wrap: break-word;
`;

const Messages = styled.div<{inline?: boolean}>`
  margin: ${({inline}) => (inline ? `0 0 0 ${Spacing.ONE}` : `0 ${Spacing.TWO}`)};
  white-space: pre-wrap;

  ${`@media (${Breakpoints.MOBILE})`} {
    margin: 0 0 0 ${Spacing.ONE};
  }
`;

const LabeledMessages = styled.div`
  display: flex;
  align-items: center;
`;

const MessageBanner = ({
  heading,
  subtext,
  type,
  actionButton,
  contextual,
  noMargin,
  dismissable,
  autoScroll,
  dismissAriaLabel,
  'data-testid': dataTestId,
  onDismiss,
  inline,
  padding,
  customVisibilityHandler,
  borderStyle,
  id
}: Props) => {
  const [visible, setVisible] = useState(true);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery({query: `(${Breakpoints.MOBILE})`});

  const handleDismiss = useCallback(() => {
    if (onDismiss) {
      onDismiss();
    }

    !customVisibilityHandler && setVisible(false);
  }, [onDismiss, customVisibilityHandler]);

  useLayoutEffect(() => {
    if (autoScroll && visible) {
      requestAnimationFrame(() => wrapperRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'}));
    }
  }, [autoScroll, visible, wrapperRef]);

  if (!visible) {
    return null;
  }

  const getBannerTheme = () => {
    switch (type) {
      case 'success':
        return TextThemes.SUCCESS;
      case 'error':
        return TextThemes.WARNING;
      case 'info':
        return TextThemes.INFO;
      default:
        return undefined;
    }
  };

  return (
    <Wrapper
      {...{id}}
      ref={autoScroll ? wrapperRef : null}
      $contextual={contextual}
      $noMargin={noMargin}
      type={type}
      borderStyle={borderStyle}
      data-testid={dataTestId}
      padding={padding}>
      <Content {...{contextual}}>
        <LabeledMessages>
          <Icon {...{type}} />
          <Messages role="status" {...{inline}}>
            {heading && (
              <BodyText type={BodyTypes.SMALLBODYSPAN} fontWeight={FontWeight.SEMI_BOLD} textTheme={getBannerTheme()}>
                {heading}
              </BodyText>
            )}
            {heading && subtext && <br />}
            {subtext && (
              <BodyText type={BodyTypes.SMALLBODYSPAN} textTheme={getBannerTheme()}>
                {subtext}
              </BodyText>
            )}
          </Messages>
        </LabeledMessages>
        {!isMobile && actionButton && <BannerActionWrapper {...{type}}>{actionButton}</BannerActionWrapper>}
        {dismissable && (
          <DismissButton
            {...{'data-testid': dataTestId ? `${dataTestId}.DismissButton` : undefined}}
            onDismiss={handleDismiss}
            dismissAriaLabel={dismissAriaLabel ? dismissAriaLabel : intlFormatSingleMessage('Close Alert')}
            {...{type}}
          />
        )}
      </Content>
      {isMobile && actionButton && <BannerActionWrapper {...{type}}>{actionButton}</BannerActionWrapper>}
    </Wrapper>
  );
};

export default MessageBanner;
